import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Star, MapPin } from "lucide-react";
import BookingForm from "@/components/landing/BookingForm";

const Hero = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 lg:pt-32 pb-12 lg:pb-16 bg-white overflow-hidden"
    >
      {/* Subtle decorative background */}
      <div className="absolute inset-0 bg-dots opacity-60 pointer-events-none" aria-hidden></div>
      <div className="absolute -top-24 -right-24 h-[420px] w-[420px] rounded-full bg-[#19A8E8]/8 blur-3xl pointer-events-none" aria-hidden></div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="lg:col-span-7"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#19A8E8]/10 text-[#19A8E8] text-xs font-semibold uppercase tracking-[0.18em]">
            <ShieldCheck className="h-3.5 w-3.5" />
            India's Trusted Cab Partner
          </div>

          <h1
            data-testid="hero-headline"
            className="mt-5 font-display font-extrabold text-[#0F172A] text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight"
          >
            Travel More.{" "}
            <span className="text-[#19A8E8]">Pay Less.</span>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-5 text-base lg:text-lg text-[#64748B] max-w-xl leading-relaxed"
          >
            Reliable Outstation Cabs, Airport Transfers, One-Way Trips &amp; Self-Drive Cars
            Across India.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Button
              asChild
              size="lg"
              data-testid="hero-book-now-btn"
              className="rounded-full bg-[#19A8E8] hover:bg-[#0E8AC4] text-white font-semibold px-7 h-12 shadow-[0_8px_24px_rgba(25,168,232,0.30)] transition-all group"
            >
              <a href="#booking">
                Book Your Ride Now
                <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              data-testid="hero-instant-fare-btn"
              className="rounded-full border-2 border-[#19A8E8] text-[#19A8E8] bg-white hover:bg-[#19A8E8] hover:text-white font-semibold px-7 h-12"
            >
              <a href="#booking">Get Instant Fare</a>
            </Button>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 text-sm text-[#64748B]">
            <div data-testid="hero-stat-trips" className="flex items-center gap-2">
              <div className="flex">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-[#19A8E8] text-[#19A8E8]" />
                ))}
              </div>
              <span className="font-display font-bold text-[#0F172A] text-base">1 Lakh+ Trips &amp; Counting</span>
            </div>
            <div data-testid="hero-stat-cities" className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[#19A8E8]" />
              <span className="font-display font-semibold text-[#0F172A]">Serving Across 17 Cities</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-5"
        >
          <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-[0_20px_50px_rgba(15,23,42,0.08)] bg-white">
            <img
              src="/fleet/hero-innova.jpg"
              alt="White Toyota Innova Crysta — Cabsab outstation taxi"
              className="w-full h-[360px] lg:h-[440px] object-cover"
              loading="eager"
            />
            <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3">
              <div className="bg-white rounded-2xl px-4 py-3 flex items-center gap-3 shadow-md border border-slate-100">
                <div className="h-10 w-10 rounded-xl bg-[#19A8E8]/10 flex items-center justify-center text-[#19A8E8]">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-[#64748B]">Verified &amp; Sanitized</div>
                  <div className="font-display font-bold text-[#0F172A] text-sm">GPS Tracked Fleet</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 mt-10 lg:mt-14">
        <BookingForm />
      </div>
    </section>
  );
};

export default Hero;
